import {
  commendaAiConversationsKey,
  type CommendaAiRunContext,
} from '@commenda/api/commenda-ai/api';
import type {
  CommendaAiConversationAccessView,
  CommendaAiPageContext,
} from '@commenda/api/commenda-ai/types';
import { useCommendaAiConversationHistory } from '@commenda/api/commenda-ai/useCommendaAiConversationHistory';
import { LoadingIndicator } from '@commenda/kakashi';
import { useEffect } from 'react';
import { mutate } from 'swr';
import { CommendaAiConversation } from './CommendaAiConversation';
import type { IComposerControlsConfig } from './composer-controls';
import { useCommendaAiWorkspace } from './useCommendaAiWorkspace';

export const CommendaAiLoading = () => (
  <div className="flex h-full items-center justify-center">
    <LoadingIndicator />
  </div>
);

/** Hydrates a conversation's history (from stored parts) before mounting the chat, so the useChat
 * hook only ever mounts with settled initial messages. This component always resumes an existing
 * conversation by id. */
export function CommendaAiLoader({
  conversationId,
  runContext,
  headerTrailing,
  composerControls,
  layout = 'page',
  renderHeader,
  contextSlot,
  getPageContext,
  onLoadingChange,
  afterDeleteHref,
  notFoundActions,
  buildSendBody,
  beforeSend,
  onGeneratingChange,
  onMessagesChange,
}: {
  conversationId: string;
  runContext?: CommendaAiRunContext;
  headerTrailing?: React.ReactNode;
  /** Forwarded to `CommendaAiConversation`, which shows the agent as the one this conversation
   * was created with. */
  composerControls?: IComposerControlsConfig;
  layout?: 'page' | 'panel';
  renderHeader?: (
    title: string,
    access?: CommendaAiConversationAccessView
  ) => React.ReactNode;
  contextSlot?: React.ReactNode;
  getPageContext?: () => CommendaAiPageContext | null;
  onLoadingChange?: (isLoading: boolean) => void;
  /** Where deleting the conversation lands the user; forwarded to `CommendaAiConversation`. */
  afterDeleteHref?: string;
  /** Rendered under the not-found message. The affiliate surface offers the other scope here,
   * since a conversation opened in the wrong scope is a 404. */
  notFoundActions?: React.ReactNode;
  /** Per-send fields a surface adds on top of the text: the builder's mode, model and target version. */
  buildSendBody?: React.ComponentProps<
    typeof CommendaAiConversation
  >['buildSendBody'];
  /** Awaited before the turn posts. The builder saves its canvas and may fork a version here. */
  beforeSend?: React.ComponentProps<
    typeof CommendaAiConversation
  >['beforeSend'];
  /** Lifts "a turn is in flight" to the host, which the builder gates its canvas sync on. */
  onGeneratingChange?: (isGenerating: boolean) => void;
  /** Forwarded to `CommendaAiConversation`: the transcript for a host that computes from it. */
  onMessagesChange?: React.ComponentProps<
    typeof CommendaAiConversation
  >['onMessagesChange'];
}) {
  const {
    companyId,
    scope,
    isLoading: isConfigLoading,
  } = useCommendaAiWorkspace();
  const {
    conversation: loadedConversation,
    messages: initialMessages,
    error,
  } = useCommendaAiConversationHistory(
    conversationId,
    runContext,
    !isConfigLoading
  );

  useEffect(() => {
    onLoadingChange?.(!initialMessages && !error);
  }, [error, initialMessages, onLoadingChange]);

  useEffect(() => {
    if (!loadedConversation) return;
    mutate(commendaAiConversationsKey(companyId, true, scope)).catch(
      () => undefined
    );
  }, [companyId, loadedConversation, scope]);

  if (error)
    return (
      <>
        {renderHeader?.('Conversation unavailable')}
        <div className="text-chat-destructive p-4 text-sm">
          Couldn&apos;t load this conversation. It may have been deleted or made
          private.
        </div>
        {notFoundActions ? (
          <div className="px-xl pb-xl">{notFoundActions}</div>
        ) : null}
      </>
    );
  if (!initialMessages) return <CommendaAiLoading />;

  return (
    <CommendaAiConversation
      conversationId={conversationId}
      initialMessages={initialMessages}
      loadedConversation={loadedConversation ?? null}
      headerTrailing={headerTrailing}
      composerControls={composerControls}
      layout={layout}
      renderHeader={renderHeader}
      contextSlot={contextSlot}
      getPageContext={getPageContext}
      afterDeleteHref={afterDeleteHref}
      {...(buildSendBody ? { buildSendBody } : {})}
      {...(beforeSend ? { beforeSend } : {})}
      {...(onGeneratingChange ? { onGeneratingChange } : {})}
      {...(onMessagesChange ? { onMessagesChange } : {})}
    />
  );
}
